import {
  useEffect, useId, useRef, useState, type ChangeEvent, type CSSProperties, type ReactNode,
} from 'react';
import { centsToInput, initials, toCents } from '@/lib/format';
import { IconAlert, IconX } from './Icons';

/* ------------------------------------------------------------------ avatar */

interface AvatarProps {
  name: string;
  color?: string | null;
  /** `photoUrl` da API; sem ela, ou se a imagem não carregar, ficam as iniciais. */
  photo?: string | null;
  size?: number;
}

export function Avatar({ name, color, photo, size = 28 }: AvatarProps) {
  // Foto que não carregou (apagada, bucket fora do ar): voltam as iniciais.
  const [broken, setBroken] = useState<string | null>(null);
  const src = photo && photo !== broken ? photo : null;
  return (
    <span
      className="avatar"
      style={{ background: color ?? 'var(--showcase-accent)', width: size, height: size, fontSize: size * 0.4 }}
      title={name}
      aria-hidden
    >
      {src ? (
        <img src={src} alt="" loading="lazy" decoding="async" draggable={false} onError={() => setBroken(src)} />
      ) : (
        initials(name)
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ fields */

interface FieldProps {
  label: string;
  children: ReactNode;
  error?: string | null;
  hint?: string;
}

export function Field({ label, children, error, hint }: FieldProps) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {error ? <span className="field-error">{error}</span> : null}
      {!error && hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}

interface MoneyInputProps {
  /** Valor em centavos. */
  value: number;
  onChange: (cents: number) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  onEnter?: () => void;
}

/**
 * Entrada monetária em reais que devolve centavos.
 * Mantém o texto digitado enquanto o campo está em foco para não atrapalhar a
 * digitação, e normaliza a formatação ao sair.
 */
export function MoneyInput({ value, onChange, placeholder = '0,00', disabled, autoFocus, onEnter }: MoneyInputProps) {
  const ref = useRef<HTMLInputElement>(null);
  const editing = useRef(false);

  useEffect(() => {
    if (!editing.current && ref.current) ref.current.value = centsToInput(value);
  }, [value]);

  return (
    <input
      ref={ref}
      className="inp num"
      inputMode="decimal"
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      defaultValue={centsToInput(value)}
      onFocus={() => {
        editing.current = true;
      }}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value.replace(/[^\d.,-]/g, '');
        event.target.value = raw;
        onChange(toCents(raw));
      }}
      onBlur={(event) => {
        editing.current = false;
        const cents = toCents(event.target.value);
        event.target.value = centsToInput(cents);
        onChange(cents);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && onEnter) {
          event.preventDefault();
          onEnter();
        }
      }}
    />
  );
}

/* ------------------------------------------------------------------- modal */

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}

export function Modal({ title, onClose, children, footer, width = 560 }: ModalProps) {
  const titleId = useId();

  // Esc fecha; o scroll da página fica travado enquanto o modal está aberto.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="modal" style={{ '--modal-width': `${width}px` } as CSSProperties}>
        <div className="modal-head">
          <h2 id={titleId} className="modal-title">{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Fechar">
            <IconX />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

interface ConfirmProps {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title, message, confirmLabel = 'Confirmar', danger, busy, onConfirm, onCancel,
}: ConfirmProps) {
  return (
    <Modal
      title={title}
      onClose={onCancel}
      width={440}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancelar</button>
          <button
            type="button"
            className={danger ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? <span className="spinner" /> : null}
            {confirmLabel}
          </button>
        </>
      }
    >
      <div style={{ fontSize: 13, lineHeight: 1.6 }}>{message}</div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ states */

export const ErrorState = ({ message }: { message: string }) => (
  <div className="alert alert-error">
    <IconAlert />
    <span>{message}</span>
  </div>
);

export const EmptyState = ({ message }: { message: string }) => <div className="empty">{message}</div>;

export function TableSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div style={{ padding: "12px 0" }}>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <div
              key={columnIndex}
              className="skeleton"
              style={{ height: 16, flex: columnIndex === 0 ? 2 : 1 }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function CardSkeleton({ height = 90 }: { height?: number }) {
  return <div className="skeleton" style={{ height }} />;
}

/* ---------------------------------------------------------------- progress */

export function ProgressBar({
  value, max, color, height = 5,
}: { value: number; max: number; color?: string; height?: number }) {
  const ratio = max > 0 ? Math.max(0, Math.min(value / max, 1)) : 0;
  const reached = max > 0 && value >= max;
  return (
    <div className="bar-track" style={{ height }}>
      <div
        className="bar-fill"
        style={{ width: `${ratio * 100}%`, background: color ?? (reached ? 'var(--success)' : 'var(--data)') }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------- stats */

/**
 * Métrica: rótulo pequeno em caixa alta sobre o valor em serifa.
 * O contraste de tamanho e de família faz o número carregar a leitura sozinho.
 */
export function StatLine({
  label, value, color, sub,
}: { label: string; value: ReactNode; color?: string; sub?: ReactNode }) {
  return (
    <div className="metric">
      <span className="label">{label}</span>
      <span className="display num metric-value" style={color ? { color } : undefined}>{value}</span>
      {sub ? <span className="metric-sub">{sub}</span> : null}
    </div>
  );
}

/** Cabeçalho de seção: título curto e um fio que corre até a margem. */
export function SectionHead({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <div className="section-head">
      <span className="section-title">{title}</span>
      <span className="section-rule" />
      {children}
    </div>
  );
}

/** Faixa de métricas separada por fios verticais. */
export const MetricRow = ({ children }: { children: ReactNode }) => (
  <div className="metric-row">{children}</div>
);

/* --------------------------------------------------------------- pagination */

export function Pagination({
  page, totalPages, total, onChange,
}: { page: number; totalPages: number; total: number; onChange: (page: number) => void }) {
  if (totalPages <= 1) {
    return (
      <div className="muted" style={{ fontSize: 12, padding: '10px 2px' }}>
        {total} {total === 1 ? 'resultado' : 'resultados'}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 2px' }}>
      <span className="muted" style={{ fontSize: 12 }}>
        {total} resultados · página {page} de {totalPages}
      </span>
      <div className="spacer" />
      <button type="button" className="btn btn-sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        Anterior
      </button>
      <button type="button" className="btn btn-sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
        Próxima
      </button>
    </div>
  );
}
